import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import ActivationCodeList from '../components/ActivationCodeList';
import ActivationBatchTable from '../components/ActivationBatchTable';
import CreateBatchDialog from '../components/CreateBatchDialog';
import { ActivationCodeService } from '../services/activationCodeService';
import toast from 'react-hot-toast';

const ActivationCodePage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [codes, setCodes] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  
  const [filters, setFilters] = useState({
    q: searchParams.get('q') || '',
    status: searchParams.get('status') || '',
    sku: searchParams.get('sku') || '',
    book_id: searchParams.get('book_id') || '',
    date_from: searchParams.get('date_from') || '',
    date_to: searchParams.get('date_to') || ''
  });

  const [batchFilters, setBatchFilters] = useState({
    q: '',
    sku: '',
    book_id: '',
    date_from: '',
    date_to: ''
  });

  const [pagination, setPagination] = useState({
    current_page: parseInt(searchParams.get('page')) || 1,
    per_page: 20,
    total: 0,
    last_page: 1
  });

  const [batchPagination, setBatchPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
    last_page: 1
  });

  // Load activation codes data
  const loadCodes = async () => {
    setLoading(true);
    try {
      const params = {
        ...filters,
        page: pagination.current_page,
        per_page: pagination.per_page
      };

      const response = await ActivationCodeService.getCodes(params);
      
      if (response.success) {
        setCodes(response.data);
        setPagination(prev => ({
          ...prev,
          total: response.pagination.total,
          last_page: response.pagination.last_page,
          from: response.pagination.from,
          to: response.pagination.to
        }));
      } else {
        throw new Error(response.message || 'Failed to load activation codes');
      }
      
    } catch (error) {
      console.error('Error loading activation codes:', error);
      toast.error('Có lỗi xảy ra khi tải dữ liệu mã kích hoạt');
    } finally {
      setLoading(false);
    }
  };

  // Load activation batches data
  const loadBatches = async () => {
    try {
      const params = {
        ...batchFilters,
        page: batchPagination.current_page,
        per_page: batchPagination.per_page
      };

      const response = await ActivationCodeService.getBatches(params);
      
      if (response.success) {
        setBatches(response.data || []);
        setBatchPagination(prev => ({
          ...prev,
          total: response.pagination?.total || 0,
          last_page: response.pagination?.last_page || 1,
          from: response.pagination?.from || 0,
          to: response.pagination?.to || 0
        }));
      } else {
        throw new Error(response.message || 'Failed to load activation batches');
      }
      
    } catch (error) {
      console.error('Error loading activation batches:', error);
      // Set empty data to prevent errors
      setBatches([]);
      setBatchPagination(prev => ({
        ...prev,
        total: 0,
        last_page: 1,
        from: 0,
        to: 0
      }));
      toast.error('Có lỗi xảy ra khi tải dữ liệu lô mã kích hoạt');
    }
  };



  // Load data
  const loadData = async () => {
    await loadCodes();
  };

  useEffect(() => {
    loadData();
    loadBatches();
  }, [pagination.current_page, batchPagination.current_page]); // Reload when page changes

  useEffect(() => {
    loadBatches();
  }, [batchFilters, batchPagination.current_page]); // Reload batches when batch filters change

  useEffect(() => {
    // Update URL params when filters change
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    if (pagination.current_page > 1) {
      params.set('page', pagination.current_page.toString());
    }
    setSearchParams(params);
    
    // Reset to page 1 when filters change
    if (pagination.current_page !== 1) {
      setPagination(prev => ({ ...prev, current_page: 1 }));
    } else {
      loadData();
    }
  }, [filters]);

  const handleCreateSuccess = () => {
    loadData(); // Reload codes
    loadBatches(); // Reload batches
  };

  const handleRevokeCodes = async (codeIds) => {
    try {
      setLoading(true);
      
      // Service tự bọc { code_ids }, chỉ truyền mảng id
      const response = await ActivationCodeService.revokeCodes(codeIds);
      
      if (response.success) {
        console.log('Codes revoked successfully:', response);
        
        // Update local state
        setCodes(prevCodes => 
          prevCodes.map(code => 
            codeIds.includes(code._id) 
              ? { ...code, status: 'revoked' }
              : code
          )
        );
        
        // Reload data to update statistics
        loadData();
        
        toast.success(`Đã thu hồi ${response.data?.revoked_count ?? codeIds.length} mã kích hoạt`);
      } else {
        throw new Error(response.message || 'Failed to revoke codes');
      }
      
    } catch (error) {
      console.error('Error revoking codes:', error);
      
      if (error.response?.data?.message) {
        toast.error(`Lỗi: ${error.response.data.message}`);
      } else if (error.message) {
        toast.error(`Lỗi: ${error.message}`);
      } else {
        toast.error('Có lỗi xảy ra khi thu hồi mã');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format, data) => {
    try {
      setLoading(true);
      
      const exportParams = {
        format: format,
        ...filters
      };

      const response = await ActivationCodeService.exportCodes(exportParams);
      
      if (response.status === 200) {
        // Create download link for the file
        const blob = new Blob([response.data], { 
          type: format === 'csv' ? 'text/csv' : 'application/pdf' 
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `activation_codes_${new Date().toISOString().split('T')[0]}.${format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success(`Đã xuất ${data.length} mã ra file ${format.toUpperCase()}`);
      } else {
        throw new Error('Export failed');
      }
      
    } catch (error) {
      console.error('Error exporting codes:', error);
      
      if (error.response?.data?.message) {
        toast.error(`Lỗi xuất file: ${error.response.data.message}`);
      } else if (error.message) {
        toast.error(`Lỗi xuất file: ${error.message}`);
      } else {
        toast.error('Có lỗi xảy ra khi xuất mã');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, current_page: page }));
  };

  const handleBatchPageChange = (page) => {
    setBatchPagination(prev => ({ ...prev, current_page: page }));
  };

  const handleRefresh = () => {
    loadData();
    loadBatches();
  };

  const handleBatchSearch = (searchTerm) => {
    setBatchFilters(prev => ({ ...prev, q: searchTerm }));
    setBatchPagination(prev => ({ ...prev, current_page: 1 }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-10xl mx-auto ">
        {/* Breadcrumb */}
        <nav className="mb-6">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to="/admin/books" className="text-blue-600 hover:text-blue-700">
                Quản lý sách
              </Link>
            </li>
            <li className="text-gray-500">/</li>
            <li className="text-gray-900 font-medium">Mã kích hoạt</li>
          </ol>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Quản lý mã kích hoạt</h1>
          <p className="text-gray-600 mt-1">
            Tạo, quản lý và theo dõi mã kích hoạt cho các sách
          </p>
        </div>








        {/* Batches Table */}
        <div className="mb-8">
          <ActivationBatchTable
            batches={batches}
            loading={loading}
            onRefresh={handleRefresh}
            pagination={batchPagination}
            onPageChange={handleBatchPageChange}
            onCreateBatch={() => setShowCreateDialog(true)}
            onSearch={handleBatchSearch}
          />
        </div>

        {/* Codes List */}
        <ActivationCodeList
          codes={codes}
          loading={loading}
          onRefresh={handleRefresh}
          onRevoke={handleRevokeCodes}
          onExport={handleExport}
          pagination={pagination}
          onPageChange={handlePageChange}
        />

        {/* Create Batch Dialog */}
        <CreateBatchDialog
          open={showCreateDialog}
          onOpenChange={setShowCreateDialog}
          onSuccess={handleCreateSuccess}
        />

      </div>
    </div>
  );
};

export default ActivationCodePage;
